import { SET_USERS, USER_UPDATE_ALERT } from "./actionTypes";
import { addDangerAlert, addSuccessAlert } from "./alerts";
import { hasAdminAccess, hasBackofficeAccess } from "../services/roles";
import {
  requestDeleteUser,
  requestFetchUsers,
  requestUpdateUser,
  requestUpdateUserRole,
} from "../services/backendRequests";

import { setRefreshing } from "./dataTable";

export const USERS_PER_PAGE = 25;
// Alphabetical (last name), also while searching.
export const DEFAULT_USERS_SORT = "lastname";

function refreshingUsers(refreshing = true) {
  return setRefreshing("users", refreshing);
}

function setUsers(users, total, page, query, filters, sort) {
  return {
    type: SET_USERS,
    users,
    total,
    page,
    query,
    filters,
    sort,
  };
}

// Fetches a page of users from the server. When `page`/`q`/`filters`/`sort` are omitted
// (e.g. a refresh after an inline action), the current values from the store are reused.
export function fetchUsers({ page, q, filters, sort } = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const nextPage = page ?? state.users.page ?? 1;
    const nextQuery = q ?? state.users.query ?? "";
    const nextFilters = filters ?? state.users.filters;
    const nextSort = sort ?? state.users.sort ?? DEFAULT_USERS_SORT;

    dispatch(refreshingUsers());
    requestFetchUsers(state.user, {
      page: nextPage,
      perPage: USERS_PER_PAGE,
      q: nextQuery,
      filters: nextFilters,
      sort: nextSort,
    })
      .then((response) => {
        dispatch(
          setUsers(
            response.users,
            response.total,
            nextPage,
            nextQuery,
            nextFilters,
            nextSort
          )
        );
        dispatch(refreshingUsers(false));
      })
      .catch((err) => dispatch(addDangerAlert(err)));
  };
}

export function changeUserRole(userToUpdate, role) {
  return (dispatch, getState) => {
    const { user } = getState();

    dispatch(refreshingUsers());
    requestUpdateUserRole(
      userToUpdate,
      {
        admin: hasAdminAccess({ role }),
        backoffice: hasBackofficeAccess({ role }),
      },
      user
    )
      .then(() => {
        dispatch(fetchUsers());
        dispatch(addSuccessAlert("User role was updated"));
      })
      .catch((err) => dispatch(addDangerAlert(err)));
  };
}

export function changeAccess(userToUpdate, accessFlag) {
  return (dispatch, getState) => {
    dispatch(refreshingUsers());
    requestUpdateUser(
      userToUpdate,
      {
        access: accessFlag,
      },
      getState().user
    )
      .then(() => {
        dispatch(fetchUsers());
        dispatch(addSuccessAlert("User access was updated"));
      })
      .catch((err) => dispatch(addDangerAlert(err)));
  };
}

function updateUserAlert(alertMessage) {
  return {
    type: USER_UPDATE_ALERT,
    updateAlert: alertMessage,
  };
}

export function resetUserAlert() {
  return (dispatch) => dispatch(updateUserAlert(""));
}

export function updateUserInformation(user) {
  return (dispatch, getState) => {
    requestUpdateUser(user.id, user, getState().user)
      .then(() => {
        dispatch(refreshingUsers());
        dispatch(fetchUsers());
      })
      .catch((err) => dispatch(updateUserAlert(err)));
  };
}

export function deleteUser(user) {
  return (dispatch, getState) => {
    requestDeleteUser(user.id, getState().user)
      .then(() => {
        const { page, total } = getState().users;
        const lastPage = Math.max(1, Math.ceil((total - 1) / USERS_PER_PAGE));
        dispatch(fetchUsers({ page: Math.min(page, lastPage) }));
      })
      .catch((err) => dispatch(addDangerAlert(err)));
  };
}
